<!-- Patient Consent Section -->
<div class="w-full">
  <!-- Content -->
  <div>
    <!-- Research Consent Section -->
    <div>
      <h3 class="text-xl font-semibold text-900 text-center">Research Data Consent</h3>
      
      <div class="mt-4 p-4 bg-gray-50 border-round border-1 border-gray-200 text-600 text-sm">
        <p class="mb-4">
          Please obtain verbal consent from the patient or guardian to use their pseudonymised data for research purposes by 
          <strong>Aston University</strong> and <strong>Ulster University</strong>.
        </p>

        <!-- Clinician Script (Collapsible) -->
        <div class="mb-4">
          <p-button 
            label="What to say to the patient/guardian" 
            [icon]="scriptExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
            styleClass="w-full p-button-text p-button-secondary justify-content-between"
            (onClick)="toggleScript()">
          </p-button>
          
          <div class="script-content" [ngClass]="{ 'expanded': scriptExpanded }">
            <div class="p-4 bg-white border-1 border-top-none border-round-bottom space-y-3 text-700">
              <p class="mb-3">"As part of our commitment to improving eye care, we are working with researchers at Aston and Ulster University to better understand myopia, or short-sightedness."</p>
              
              <p class="mb-3">"To do this, we would like to ask for your permission to share some of your/your child's clinical data with them for their research. This data would be 'pseudonymised', which means your name and any personal contact details are removed and replaced with a code, so you cannot be directly identified."</p>
              
              <p class="mb-3">"Taking part is completely voluntary. Your decision will not affect the quality of care you receive from us, and you will still get your PreMO dashboard as part of your consultation."</p>
              
              <p class="mb-3">"Do you have any questions? If you'd like more detailed information, I can show you a QR code you can scan with your phone."</p>
              
              <p class="font-semibold">"Based on this, do you give your consent for us to share this pseudonymised data with the universities for their research?"</p>
            </div>
          </div>
        </div>
        
        <!-- Resources Section -->
        <div class="pt-4">
          <p class="font-medium text-700 mb-2">For the Patient/Guardian:</p>
          <p class="mb-3">To provide more details, show them the QR code to access the patient information sheet.</p>
          
          <p-button 
            label="Show Patient QR Code" 
            icon="pi pi-qrcode" 
            styleClass="p-button-outlined p-button-sm"
            (onClick)="showQrModal()">
          </p-button>
        </div>
      </div>

      <!-- Consent Radio Buttons -->
      <div *ngIf="form" [formGroup]="form">
        <div class="mt-6">
          <p class="font-medium text-800 mb-4">Please record the patient's decision:</p>
          
          <div class="field-radiobutton mb-4">
            <p-radioButton 
              inputId="consent-given" 
              name="consentStatus" 
              value="given" 
              formControlName="consentStatus">
            </p-radioButton>
            <label for="consent-given" class="ml-3 text-sm font-medium text-700 cursor-pointer">
              Consent Given: Verbal consent was obtained.
            </label>
          </div>
          
          <div class="field-radiobutton">
            <p-radioButton 
              inputId="consent-not-given" 
              name="consentStatus" 
              value="not-given" 
              formControlName="consentStatus">
            </p-radioButton>
            <label for="consent-not-given" class="ml-3 text-sm font-medium text-700 cursor-pointer">
              Consent Not Given: The patient/guardian declined.
            </label>
          </div>

          <!-- Validation message -->
          <div *ngIf="consentStatusControl?.invalid && consentStatusControl?.touched" class="mt-2">
            <small class="p-error">Please select a consent option to continue.</small>
          </div>
        </div>
      </div>

      <!-- Consent Outcome -->
      <div class="mt-6 p-4 bg-gray-50 border-round border-1 border-gray-200 text-600 text-sm">
        <p class="mb-2">
          <span class="font-semibold text-800">If consent is given:</span> The data will be included in the research dataset.
        </p>
        <p>
          <span class="font-semibold text-800">If consent is NOT given:</span> The data will still be used to generate the PreMO dashboard for clinical care, but will 
          <strong class="text-red-600">not</strong> be released to the universities for research.
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div class="mt-8 pt-6 border-top-1">
      <p-button 
        label="Update Consent" 
        [disabled]="!isFormValid"
        [styleClass]="isFormValid ? 'w-full p-button-success' : 'w-full p-button-secondary'"
        (onClick)="onSaveConsent()">
      </p-button>
    </div>
  </div>
</div>

<!-- QR Code Modal -->
<p-dialog 
  [(visible)]="qrModalVisible" 
  [modal]="true" 
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="qr-modal"
  header="Scan for More Information">
  
  <div class="text-center">
    <div #qrCodeContainer class="flex justify-content-center mb-4"></div>
    <p class="text-sm text-500">Scan this code with a mobile device to view the patient information sheet.</p>
  </div>
</p-dialog>